import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, 
  Droplet, 
  Utensils, 
  Pill, 
  Sparkles, 
  BookOpen,
  User,
  Menu,
  X,
  LogOut,
  Shield,
  Crown,
  TrendingUp,
  Activity,
  Moon,
  Sun,
  Heart,
  Brain,
  Users,
  FileText,
  ShoppingCart,
  Trophy,
  Target,
  Gift,
  BarChart3,
  Globe
} from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const navigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('nav.glucose'), href: '/glucose', icon: Droplet },
    { name: t('nav.meals'), href: '/meals', icon: Utensils },
    { name: t('nav.medications'), href: '/medications', icon: Pill },
    { name: t('nav.exercise'), href: '/exercise', icon: Activity },
    { name: t('nav.emotions', 'Emotions'), href: '/emotions', icon: Heart, badge: 'New' },
    { name: t('nav.vitals', 'Vitals'), href: '/vitals', icon: Activity, badge: 'New' },
    { name: t('nav.challenges', 'Challenges'), href: '/challenges', icon: Target },
    { name: t('nav.achievements', 'Achievements'), href: '/achievements', icon: Trophy },
    { name: t('nav.reports'), href: '/weekly-report', icon: BarChart3 },
    { name: t('nav.insights'), href: '/insights', icon: Sparkles },
    { name: t('nav.aiFeatures', 'AI Features'), href: '/ai-features', icon: Brain, badge: 'Pro' },
    { name: t('nav.foodScanner'), href: '/food-scanner', icon: Activity },
    { name: t('nav.recipes', 'Recipes'), href: '/recipes', icon: BookOpen },
    { name: t('nav.shop'), href: '/shop', icon: ShoppingCart },
    { name: t('nav.referral', 'Referral'), href: '/referral', icon: Gift, badge: 'Earn $' },
    { name: t('nav.community'), href: '/community', icon: Users },
    { name: t('nav.pricing'), href: '/pricing', icon: Crown },
    { name: t('nav.profile'), href: '/profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-slate-900 transition-colors duration-300">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white dark:bg-gray-800 shadow-xl transform transition-all duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-lg flex items-center justify-center">
                <Droplet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
                DiaWise
              </span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 dark:text-gray-300" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive 
                      ? 'bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/30 dark:to-blue-900/30 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600 dark:text-emerald-400' : ''}`} />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {item.badge && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-orange-400 to-pink-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Theme Toggle & Language Switcher */}
          <div className="px-4 pb-4 space-y-2">
            <LanguageSwitcher />
            <Button
              onClick={toggleTheme}
              variant="outline"
              className="w-full justify-start dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
            >
              {theme === 'dark' ? (
                <><Sun className="w-4 h-4 mr-2" /> Light Mode</>
              ) : (
                <><Moon className="w-4 h-4 mr-2" /> Dark Mode</>
              )}
            </Button>
          </div>

          {/* User info & Logout */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email || ''}</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="outline"
              className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Menu className="w-6 h-6 dark:text-gray-300" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {navigation.find(item => item.href === location.pathname)?.name || t('nav.dashboard')}
            </h1>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
        
        {/* Footer with policy links */}
        <footer className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-2">© 2024 DiaWise. All rights reserved.</p>
            <div className="flex justify-center flex-wrap gap-x-4 gap-y-1">
              <a href="/privacy-policy" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Privacy Policy
              </a>
              <span className="hidden sm:inline">•</span>
              <a href="/terms" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Terms of Service
              </a>
              <span className="hidden sm:inline">•</span>
              <a href="/cookie-policy" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Cookie Policy
              </a>
              <span className="hidden sm:inline">•</span>
              <a href="/contact" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
